import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import React from "react";
import HighChart from "./Highchart";

const usePerformanceGaugeStyles = makeThemedStyles(
  "PerformanceGauge",
  (theme) => ({
    chartTitleStyle: {
      fontFamily: "Roboto, Helvetica",
      fontSize: "1.3em",
      transform: "translateY(-20px)",
    },
  })
);

export const PerformanceGauge = React.memo(
  (
    props: React.PropsWithChildren<{
      element: ICardElementWithChildren;
      valueKey?: keyof ICardElementWithChildren;
      prettyKey?: keyof ICardElementWithChildren;
      angleStart?: number;
      angleEnd?: number;
      width?: number;
      height?: string | number;
      disableAnimation?: boolean;
    }>
  ) => {
    const styles = usePerformanceGaugeStyles();
    const { element: el } = props;

    const valueKey: keyof ICardElementWithChildren = props.valueKey || "PERF";
    const prettyKey: keyof ICardElementWithChildren =
      props.prettyKey || "PERF_prettyvalue";

    const startAngle = props.angleStart || 0;
    const endAngle = props.angleEnd || 360;

    const hcOptions = React.useMemo(() => {
      if (!el) {
        return {};
      }
      return {
        credits: {
          enabled: false,
        },
        chart: {
          width: props.width || 120,
          height: props.height,
          type: "solidgauge",
          backgroundColor: "rgba(255,255,255,0)",
        },
        title: {
          useHTML: true,
          verticalAlign: "bottom",
          floating: true,
          text: `<div class="${styles.chartTitleStyle}" style="color:#${
            el.rangecolor
          }">${el[prettyKey] || "0 %"}</div>`,
        },
        pane: {
          center: ["50%", "85%"],
          size: "140%",
          startAngle: startAngle,
          endAngle: endAngle,
          background: [
            {
              outerRadius: "112%",
              innerRadius: "83%",
              backgroundColor: `#${el.rangecolor || "333333"}22`,
              borderWidth: 0,
              shape: "arc",
              height: "50%",
            },
          ],
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickPositions: [],
        },
        plotOptions: {
          series: {
            animation: !props.disableAnimation,
          },
          solidgauge: {
            dataLabels: {
              enabled: false,
            },
            linecap: "round",
            stickyTracking: false,
            rounded: false,
          },
        },
        tooltip: {
          useHTML: true,
          formatter: function () {
            return `${t("Actual")} ${el[prettyKey]}`;
          },
        },
        series: [
          {
            name: t("Actual"),
            data: [
              {
                color: {
                  linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                  stops: [
                    ...(el[valueKey]
                      ? [
                          [0, `#${el.rangecolor || "gray"}ff`],
                          [1, `#${el.rangecolor || "gray"}99`],
                        ]
                      : [
                          [0, `#${el.rangecolor || "gray"}00`],
                          [1, `#${el.rangecolor || "gray"}00`],
                        ]),
                  ],
                },
                radius: "112%",
                innerRadius: "83%",
                y: Number(el[valueKey] || 0),
              },
            ],
          },
        ],
      };
    }, [el]);

    if (!el) return null;
    return <HighChart chartOptions={hcOptions} />;
  }
);
